import { useState, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { motion } from 'motion/react';
import { ArrowLeft, Heart, MessageCircle, Share2, Megaphone, MoreHorizontal, MapPin, Lock, Pin, Trash2, Archive, Copy, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { ShareYapModal } from './ShareYapModal';
import { useYapInteractions } from './YapInteractionsContext';
import { FlagImage } from './utils/countryFlags';
import { PostMenu } from './ui/PostMenu';
import { copyToClipboard } from './utils/clipboard';
import { EmbeddedYap } from './EmbeddedYap';
import { ParallaxBackground } from './utils/parallax';
import { ImageWithPreview } from './ImageWithPreview';
import { YapService } from '../services/api';
import { formatTimeAgo } from './utils/timeFormat';
import { getYapAuthorInfo, getYapInteractionId, getOriginalYapId, isShoutedYap, getYapPostType } from './utils/yapUtils';
import { EVENTS, SCREENS } from '../constants/navigation';
import { FormattedText } from './FormattedText';
import { PollCard } from './ui/PollCard';
import { LinkPreview } from './LinkPreview';
import { extractFirstUrl } from './utils/textParsers';
import { LikeButton } from './ui/LikeButton';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { ExpandableText } from './ui/ExpandableText';

interface UserYapsScreenProps {
  onBack: () => void;
}

// Helper to parse timestamp for sorting
const getTimestampMs = (timeVal: any): number => {
  if (!timeVal) return 0;
  
  if (typeof timeVal === 'number') return timeVal;
  
  if (typeof timeVal === 'string') {
    if (timeVal === 'Just now' || timeVal === 'just now' || timeVal === 'Now' || timeVal === 'now') return Date.now();
    const date = new Date(timeVal);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  return 0;
};

export function UserYapsScreen({ onBack }: UserYapsScreenProps) {
  const { userProfile, shoutedYaps: userShoutedYaps, removeShout, isTribeMember, isForumMember } = useUserData();
  const { filterVisible } = useContentFilter();
  const { toggleLike, isYapLiked, isYapShouted, getLikeCount, getShoutCount, isPostLiked, getPostLikeCount } = useYapInteractions();
  const [yaps, setYaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showShareYap, setShowShareYap] = useState(false);
  const [selectedYapToShare, setSelectedYapToShare] = useState<any>(null);

  useScreenReady(!loading);

  // Track which user yaps are pinned
  const [localPinnedYaps, setLocalPinnedYaps] = useState<string[]>([]);

  // Fetch user yaps on mount
  useEffect(() => {
    const fetchYaps = async () => {
      if (!userProfile?.id) {
        setLoading(false);
        return;
      }
      try {
        const fetchedYaps = await YapService.fetchUserYaps(String(userProfile.id));
        // Filter out anonymous yaps
        const filteredYaps = fetchedYaps.filter(yap => !yap.isAnonymous);

        // Initialize local pinned yaps from fetched data
        const pinnedIds = filteredYaps.filter(yap => yap.isPinned).map(yap => yap.id);
        setLocalPinnedYaps(pinnedIds);

        setYaps(filteredYaps);
      } catch (error) {
        console.error("Error fetching user yaps:", error);
        toast.error("Failed to load yaps");
      } finally {
        setLoading(false);
      }
    };
    fetchYaps();
  }, [userProfile?.id]);

  useEffect(() => {
    const handleYapDeleted = (e: CustomEvent) => {
      const { yapId } = e.detail || {};
      if (yapId) {
        setYaps(prev => prev.filter(y => y.id !== yapId));
        setLocalPinnedYaps(prev => prev.filter(id => id !== yapId));
      }
    };
    window.addEventListener('yapDeleted', handleYapDeleted as EventListener);
    return () => {
      window.removeEventListener('yapDeleted', handleYapDeleted as EventListener);
    };
  }, []);

  const handlePinYap = async (yapId: string) => {
    const isPinned = localPinnedYaps.includes(yapId);

    // Check if trying to pin and already have 3 pinned yaps
    if (!isPinned && localPinnedYaps.length >= 3) {
      toast.error('You can only pin up to 3 yaps. Unpin one first.');
      return;
    }

    // Optimistic update
    setLocalPinnedYaps((prev) =>
      isPinned ? prev.filter((id) => id !== yapId) : [...prev, yapId]
    );

    // Update backend
    try {
      await YapService.pinYap(yapId, !isPinned);
      toast.success(isPinned ? 'Yap unpinned' : 'Yap pinned to profile');

      // Update local yaps state to reflect the change for sorting
      setYaps(prev => prev.map(yap =>
        yap.id === yapId ? { ...yap, isPinned: !isPinned } : yap
      ));
    } catch (error) {
      console.error("Error pinning yap:", error);
      toast.error("Failed to update pin status");
      // Revert optimistic update
      setLocalPinnedYaps((prev) =>
        isPinned ? [...prev, yapId] : prev.filter((id) => id !== yapId)
      );
    }
  };

  // Combine user's yaps and shouted yaps
  const shoutedYapsFromContext = userShoutedYaps.map((shout) => ({
    id: shout.id,
    content: shout.originalYap.content,
    timestamp: shout.timestamp,
    timeValue: getTimestampMs(shout.timestamp || shout.originalYap.timestamp),
    likes: shout.likes,
    comments: shout.comments,
    shouts: shout.originalYap.shouts,
    isPinned: localPinnedYaps.includes(shout.id),
    visibility: shout.originalYap.friendsOnly ? 'friends' : 'public',
    isOwnYap: false,
    isShout: true,
    type: 'shouted', // Added for getYapPostType detection
    originalYap: shout.originalYap,
    shoutedBy: userProfile.id, // Added for detection
    shoutCaption: shout.shoutCaption,
    shoutImages: shout.shoutImages,
    shoutTimestamp: shout.timestamp,
    originalAuthor: shout.originalYap.author || shout.originalYap.username || 'Unknown',
    originalAvatar: shout.originalYap.avatar || '',
    originalUsername: shout.originalYap.username || '@unknown',
  }));

  const userYapsWithTime = yaps.map((yap) => ({
    ...yap,
    timeValue: getTimestampMs(yap.timestamp || yap.time),
    isPinned: localPinnedYaps.includes(yap.id),
  }));

  // 🚫 This screen lists the viewer's OWN posts, so their own content is never
  // the problem — but a shout embeds someone else's yap, so a blocked or muted
  // author reaches the screen through the viewer's own shout of them.
  // filterVisible inspects the nested originalYap, not just the shouter.
  const allYaps = filterVisible([...userYapsWithTime, ...shoutedYapsFromContext]);

  // Sort yaps: Pinned first, then by timeValue (descending)
  const sortedYaps = [...allYaps].sort((a, b) => {
    const isAPinned = localPinnedYaps.includes(a.id);
    const isBPinned = localPinnedYaps.includes(b.id);

    if (isAPinned && !isBPinned) return -1;
    if (!isAPinned && isBPinned) return 1;
    return (b.timeValue || 0) - (a.timeValue || 0);
  });

  const handleArchiveYap = (yapId: string) => {
    setYaps((prev) => prev.filter((yap) => yap.id !== yapId));
    toast.success('Yap archived');
  };

  const handleCopyLink = async (yapId: string) => {
    const success = await copyToClipboard(`https://afroyap.com/yap/${yapId}`);
    if (success) {
      toast.success('Link copied to clipboard');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const handleDeleteYap = async (yapId: string) => {
    try {
      await YapService.deleteYap(yapId);
      setYaps((prev) => prev.filter((yap) => yap.id !== yapId));
      setLocalPinnedYaps((prev) => prev.filter((id) => id !== yapId));
      toast.success('Yap deleted');
      // trigger event to refresh ProfileScreen explicitly just in case
      window.dispatchEvent(new CustomEvent('yapUpdated'));
    } catch (e) {
      toast.error('Failed to delete yap');
    }
  };

  const handleDeleteShout = async (originalYapId: string) => {
    try {
      await removeShout(originalYapId);
      toast.success('Shout deleted');
      // context automatically provides new shoutedYaps
    } catch (e) {
      toast.error('Failed to delete shout');
    }
  };

  const handleYapClick = (yap: any) => {
    // Navigate to yap detail screen
    window.dispatchEvent(new CustomEvent('navigateToYapDetail', {
      detail: {
        ...yap,
        author: userProfile.name,
        avatar: userProfile?.portfolio?.[userProfile?.profilePictureIndex || 0] || '',
        username: userProfile.username
      }
    }));
  };

  const handleShoutClick = (shout: any) => {
    // Navigate to shout detail screen
    window.dispatchEvent(new CustomEvent('navigateToShoutDetail', {
      detail: shout
    }));
  };

  // Counts derive from the filtered list so the header can never claim more
  // posts than are rendered below it.
  const visibleShoutCount = allYaps.filter((y: any) => y.isShout).length;
  const visiblePostCount = allYaps.length - visibleShoutCount;
  const totalYapCount = allYaps.length;

  return (
    // No pb-20 on the root, unlike the screens that sit behind the bottom
    // nav: /profile/yaps is not one of BOTTOM_NAV_PATHS, so that padding reserved
    // 80px of the viewport for a bar that never renders and the scrolling
    // list below was clipped 80px short of the bottom.
    <div className="w-full h-screen bg-[#F5F5DC] dark:bg-[#0D0D0D] relative">
      <ParallaxBackground speed={0.12} />
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="px-4 pb-4 border-b border-black/10 dark:border-white/10 bg-[#F5F5DC] dark:bg-[#121212]"
          style={{ paddingTop: 'max(80px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-[#2C3E50] dark:text-[#f5f5f5] hover:text-black dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-2xl text-black dark:text-white">My Yaps</h1>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">{totalYapCount} total yaps ({visiblePostCount} posts, {visibleShoutCount} shouts)</p>
            </div>
          </div>
        </div>

        {/* Yaps Feed */}
        <ScrollArea className="flex-1 h-full">
          <div className="p-4 space-y-4">
            {sortedYaps.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <p className="text-[#666] dark:text-[#E5E5E5] text-sm">No yaps yet</p>
              </div>
            )}
            
            {sortedYaps.length > 0 && sortedYaps.map((yap) => {
                const isGroupInteractionAllowed = 
                  (!yap.tribeId || isTribeMember(yap.tribeId)) && 
                  (!yap.forumId || isForumMember(yap.forumId));

                return (
                  <motion.div
                    key={yap.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-2xl border transition-all overflow-hidden card-shadow ${localPinnedYaps.includes(yap.id)
                      ? 'bg-linear-to-br from-[#F39C12]/10 to-[#E74C3C]/10 border-[#F39C12] dark:bg-linear-to-br dark:from-[#4478ea]/20 dark:to-[#2d1ef1]/20 dark:border-[#4478ea]'
                      : 'bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/5 dark:border-white/10'
                      }`}
                  >
                    {yap.isShout ? (
                      <div 
                        className="p-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        onClick={() => handleShoutClick(yap)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-bold text-black dark:text-white">{userProfile.username}</span>
                              <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo(yap.shoutTimestamp)}</span>
                            </div>
                            
                            <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#4478ea] dark:text-[#4478ea]">
                              <Megaphone className="w-3.5 h-3.5 fill-[#4478ea]" />
                              <span>Shouted</span>
                            </div>
                          </div>

                          {/* Shared menu. Archive is profile-only, which is
                              why the module keeps it surface-specific. Delete
                              routes to the shout or yap handler depending on
                              what this row actually is. */}
                          <PostMenu
                            post={yap as any}
                            surface="profile"
                            collectionName={(yap as any)?.isShout ? 'shouts' : 'yaps'}
                            onArchive={() => handleArchiveYap(yap.id)}
                            onDeleteRequested={() => (yap as any)?.isShout
                              ? handleDeleteShout(getOriginalYapId(yap))
                              : handleDeleteYap(yap.id)}
                            onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                          />
                        </div>

                        {yap.shoutCaption && (
                          <FormattedText 
                            text={yap.shoutCaption} 
                            className="text-[#666666] dark:text-[#E5E5E5] mb-3 leading-relaxed block" 
                          />
                        )}
                        {yap.shoutCaption && extractFirstUrl(yap.shoutCaption) && (
                          <div className="mb-3">
                            <LinkPreview url={extractFirstUrl(yap.shoutCaption)!} />
                          </div>
                        )}

                        {yap.shoutImages && yap.shoutImages.length > 0 && (
                          <div className={`mb-3 gap-2 ${yap.shoutImages.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-2'}`}>
                            {yap.shoutImages.map((img: string, idx: number) => (
                              <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-full">
                                <ImageWithPreview
                                  src={img}
                                  alt={`Shout image ${idx + 1}`}
                                  className="w-full h-auto object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        <EmbeddedYap
                          yapId={getOriginalYapId(yap)}
                          username={yap.originalUsername || yap.originalYap?.username || '@unknown'}
                          authorName={yap.originalAuthor || yap.originalYap?.author || 'Unknown'}
                          avatar={yap.originalYap?.avatar || yap.originalYap?.userAvatar || ''}
                          country={yap.originalYap?.countryFlag || yap.originalYap?.country}
                          timestamp={yap.originalYap?.timestamp || yap.originalYap?.time || ''}
                          content={yap.originalYap?.content || ''}
                          poll={(yap.originalYap as any)?.poll}
                          images={yap.originalYap?.images || (yap.originalYap?.image ? [yap.originalYap.image] : [])}
                          image={yap.originalYap?.image}
                          likes={yap.originalYap?.likes || 0}
                          comments={yap.originalYap?.comments || 0}
                          shouts={yap.originalYap?.shouts || 0}
                          isLiked={isPostLiked(getOriginalYapId(yap), 'yap')}
                          isShout={true}
                          isDeleted={(yap as any).originalYap?.deleted}
                          showActions={false}
                          onYapClick={(e) => {
                            e?.stopPropagation();
                            if (yap.originalYap) {
                              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: yap.originalYap }));
                            }
                          }}
                          maxLines={3}
                          minimal={true}
                        />

                        <div className="flex items-center gap-6 mt-3">
                          <LikeButton
                            postId={yap.id}
                            postType={getYapPostType(yap)}
                            originalLikeCount={yap.likes || 0}
                            canInteract={() => isGroupInteractionAllowed}
                            onBlockedInteraction={() => {
                              toast.error("Members only", { description: `Join this ${yap.tribeId ? 'tribe' : 'forum'} to like yaps.` });
                            }}
                          />
                          <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleYapClick(yap);
                            }}
                            className="flex items-center gap-2 text-[#666666] dark:text-[#a0a0a0] hover:text-[#F39C12] dark:hover:text-[#F39C12] transition-colors touch-clickable"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span className="text-sm">{yap.comments}</span>
                          </motion.button>
                          <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setSelectedYapToShare(yap);
                              setShowShareYap(true);
                            }}
                            className="flex items-center gap-2 text-[#666666] dark:text-[#a0a0a0] hover:text-[#F39C12] dark:hover:text-[#F39C12] transition-colors touch-clickable"
                          >
                            <Share2 className="w-5 h-5" />
                          </motion.button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="p-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        onClick={() => handleYapClick(yap)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-bold text-black dark:text-white">{userProfile.username}</span>
                              <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo(yap.timestamp || 'Just now')}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              {yap.isPinned && (
                                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F39C12] text-white font-bold uppercase tracking-wider">
                                  <Pin className="w-2.5 h-2.5" />
                                  Pinned
                                </span>
                              )}
                              {yap.visibility === 'friends' && (
                                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#E74C3C]/20 dark:bg-[#DC2626]/30 text-[#E74C3C] dark:text-[#DC2626] font-bold uppercase tracking-wider">
                                  <Lock className="w-2.5 h-2.5" />
                                  Friends
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Shared menu. Archive is profile-only, which is
                              why the module keeps it surface-specific. Delete
                              routes to the shout or yap handler depending on
                              what this row actually is. */}
                          <PostMenu
                            post={yap as any}
                            surface="profile"
                            collectionName={(yap as any)?.isShout ? 'shouts' : 'yaps'}
                            onArchive={() => handleArchiveYap(yap.id)}
                            onDeleteRequested={() => (yap as any)?.isShout
                              ? handleDeleteShout(getOriginalYapId(yap))
                              : handleDeleteYap(yap.id)}
                            onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                          />
                        </div>

                        <ExpandableText
                          text={yap.content}
                          className="text-[#666666] dark:text-[#E5E5E5] mb-4 leading-relaxed block"
                        />
                        
                        {extractFirstUrl(yap.content) && (
                          <div className="mb-4">
                            <LinkPreview url={extractFirstUrl(yap.content)!} />
                          </div>
                        )}

                        {(yap as any).poll?.options?.length > 0 && (
                          <div className="mb-4">
                            <PollCard yapId={String(yap.id)} poll={(yap as any).poll} />
                          </div>
                        )}

                        {(yap.image || (yap.images && yap.images.length > 0)) && (
                          <div className={`mb-4 ${(yap.images && yap.images.length >= 2) || (yap.image && yap.images && yap.images.length >= 1) ? 'grid grid-cols-2' : 'grid grid-cols-1'}`}>
                            {yap.images && yap.images.length > 0 ? (
                              yap.images.slice(0, 2).map((img: string, idx: number) => (
                                <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-full">
                                  <ImageWithPreview
                                    src={img}
                                    alt={`Yap content ${idx + 1}`}
                                    className="w-full h-auto object-cover"
                                  />
                                </div>
                              ))
                            ) : (
                                <div className="w-full rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                                  <ImageWithPreview
                                    src={yap.image!}
                                    alt="Yap content"
                                    className="w-full h-auto object-cover"
                                  />
                                </div>
                            )}
                          </div>
                        )}

                        {isShoutedYap(yap) && yap.originalYap && (
                          <div className="mt-2 mb-4">
                            <EmbeddedYap
                              yapId={getOriginalYapId(yap)}
                              username={yap.originalUsername || yap.originalYap?.username || '@unknown'}
                              authorName={yap.originalAuthor || yap.originalYap?.author || 'Unknown'}
                              avatar={yap.originalYap?.avatar || yap.originalYap?.userAvatar || ''}
                              country={yap.originalYap?.countryFlag || yap.originalYap?.country}
                              timestamp={yap.originalYap?.timestamp || yap.originalYap?.time || ''}
                              content={yap.originalYap?.content || ''}
                              poll={(yap.originalYap as any)?.poll}
                              images={yap.originalYap?.images || (yap.originalYap?.image ? [yap.originalYap.image] : [])}
                              image={yap.originalYap?.image}
                              likes={yap.originalYap?.likes || 0}
                              comments={yap.originalYap?.replies || yap.originalYap?.comments || 0}
                              shouts={yap.originalYap?.shouts || 0}
                              isLiked={isPostLiked(getOriginalYapId(yap), 'yap')}
                              isShout={true}
                              isDeleted={(yap as any).originalYap?.deleted}
                              showActions={false}
                              onYapClick={(e) => {
                                e?.stopPropagation();
                                if (yap.originalYap) {
                                  window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: yap.originalYap }));
                                }
                              }}
                              maxLines={3}
                              minimal={true}
                            />
                          </div>
                        )}

                        <div className="flex items-center gap-6">
                          <LikeButton
                            postId={yap.id}
                            postType={getYapPostType(yap)}
                            originalLikeCount={yap.likes || 0}
                            canInteract={() => isGroupInteractionAllowed}
                            onBlockedInteraction={() => {
                              toast.error("Members only", { description: `Join this ${yap.tribeId ? 'tribe' : 'forum'} to like yaps.` });
                            }}
                          />

                          <div className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5]">
                            <Megaphone className="w-5 h-5" />
                            <span className="text-sm">{getShoutCount(yap.shouts || 0, yap.id)}</span>
                          </div>

                          <div className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] cursor-pointer" onClick={() => handleYapClick(yap)}>
                            <MessageCircle className="w-5 h-5" />
                            <span className="text-sm">{yap.comments}</span>
                          </div>

                          {!yap.tribeId && !yap.forumId && (
                            <motion.button
                              whileTap={{ scale: 0.95 }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setSelectedYapToShare(yap);
                                setShowShareYap(true);
                              }}
                              className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] transition-colors touch-clickable"
                            >
                              <Share2 className="w-5 h-5" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            
            <div className="h-32" /> 
          </div>
        </ScrollArea>
      </div>

      {/* Share Yap Modal */}
      {showShareYap && selectedYapToShare && (
        <ShareYapModal
          isOpen={showShareYap}
          yap={selectedYapToShare}
          onClose={() => {
            setShowShareYap(false);
            setSelectedYapToShare(null);
          }}
        />
      )}
    </div>
  );
}
